/**
 * 一键吃鸡 Store（特殊音效预设）
 * 
 * 功能:
 * - 针对游戏场景的 EQ 预设管理
 * - 支持多种预设类型（枪声增强、脚步增强等）
 * - 提供预设的增益等级调整 (-3 ~ +3)
 * - 与 EQ Store 联动应用预设
 */
import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { hidManager } from '@/api/device/hid'
import { buildEqCurveCommands } from '@/api/device/commands'
import type { EqDataPoint } from '@/api/device/types'
import { useDeviceStore } from './device'

/**
 * 预设类型
 */
export type ChijiPresetType = 'gun' | 'footstep' | 'custom'

/**
 * 吃鸡预设接口
 */
export interface ChijiPreset {
  id: string
  name: string
  type: ChijiPresetType
  icon?: string
  // 枪声频点 (低频): 20, 50, 75, 150, 300, 600, 1000 Hz
  gunPoints: EqDataPoint[]
  // 脚步频点 (高频): 2000, 3000, 6000, 8000, 10000, 12000, 16000 Hz
  footstepPoints: EqDataPoint[]
  // 等级 -3 ~ +3 (对应增益 -9 ~ +9)
  gunLevel: number
  footstepLevel: number
}

// 默认枪声频点
const defaultGunFrequencies = [20, 50, 75, 150, 300, 600, 1000]
// 默认脚步频点
const defaultFootstepFrequencies = [2000, 3000, 6000, 8000, 10000, 12000, 16000]

// 等级对应的增益值
const levelToGain = [-9, -6, -3, 0, 3, 6, 9]

// 创建默认预设数据点
function createDefaultPoints(frequencies: number[]): EqDataPoint[] {
  return frequencies.map((freq, index) => ({
    fid: index + 1,
    freq: freq >= 1000 ? `${freq / 1000}K` : `${freq}Hz`,
    gain: 0,
    q: '0.75',
  }))
}

// 默认预设
const defaultPresets: ChijiPreset[] = [
  {
    id: 'balanced',
    name: '均衡模式',
    type: 'gun',
    icon: 'Scale',
    gunPoints: createDefaultPoints(defaultGunFrequencies),
    footstepPoints: createDefaultPoints(defaultFootstepFrequencies),
    gunLevel: 3,
    footstepLevel: 3,
  },
  {
    id: 'gun-focus',
    name: '枪声增强',
    type: 'gun',
    icon: 'Aim',
    gunPoints: createDefaultPoints(defaultGunFrequencies),
    footstepPoints: createDefaultPoints(defaultFootstepFrequencies),
    gunLevel: 5,
    footstepLevel: 2,
  },
  {
    id: 'footstep-focus',
    name: '脚步声增强',
    type: 'footstep',
    icon: 'Bell',
    gunPoints: createDefaultPoints(defaultGunFrequencies),
    footstepPoints: createDefaultPoints(defaultFootstepFrequencies),
    gunLevel: 2,
    footstepLevel: 5,
  },
  {
    id: 'competition',
    name: '竞技模式',
    type: 'gun',
    icon: 'Trophy',
    gunPoints: createDefaultPoints(defaultGunFrequencies),
    footstepPoints: createDefaultPoints(defaultFootstepFrequencies),
    gunLevel: 4,
    footstepLevel: 4,
  },
]

export const useChijiStore = defineStore('chiji', () => {
  const deviceStore = useDeviceStore()

  // State
  const enabled = ref(false)
  const currentPresetId = ref<string>('balanced')
  const customPresets = ref<ChijiPreset[]>([])
  const customCounter = ref(1)

  // Getters
  const currentPreset = computed(() => {
    const all = [...defaultPresets, ...customPresets.value]
    return all.find(p => p.id === currentPresetId.value) || defaultPresets[0]
  })

  const allPresets = computed(() => [...defaultPresets, ...customPresets.value])

  const isCustomPreset = computed(() => {
    return currentPreset.value?.type === 'custom'
  })

  // 计算实际增益值
  const gunGain = computed(() => levelToGain[currentPreset.value?.gunLevel ?? 3])
  const footstepGain = computed(() => levelToGain[currentPreset.value?.footstepLevel ?? 3])

  // Actions
  async function setEnabled(value: boolean) {
    enabled.value = value
    if (value) {
      // 对齐旧版: 开启"一键吃鸡"前，如果EQ开着，会先关掉EQ（互斥）
      try {
        const { useEqStore } = await import('./eq')
        const eqStore = useEqStore()
        if (eqStore.isEnabled) {
          await eqStore.setEqEnabled(false)
        }
      } catch {
        // ignore: eq store might not be available in some builds
      }
      applyCurrentPreset()
    } else {
      resetChiji()
    }
  }

  function selectPreset(presetId: string) {
    currentPresetId.value = presetId
    if (enabled.value) {
      applyCurrentPreset()
    }
  }

  function setGunLevel(level: number) {
    if (currentPreset.value && level >= 0 && level < levelToGain.length) {
      currentPreset.value.gunLevel = level
      if (enabled.value) {
        applyCurrentPreset()
      }
    }
  }

  function setFootstepLevel(level: number) {
    if (currentPreset.value && level >= 0 && level < levelToGain.length) {
      currentPreset.value.footstepLevel = level
      if (enabled.value) {
        applyCurrentPreset()
      }
    }
  }

  function addCustomPreset(name: string, basePreset?: ChijiPreset) {
    const newPreset: ChijiPreset = {
      id: `custom-${Date.now()}`,
      name: name || `自定义${customCounter.value}`,
      type: 'custom',
      icon: 'Star',
      gunPoints: basePreset
        ? [...basePreset.gunPoints]
        : createDefaultPoints(defaultGunFrequencies),
      footstepPoints: basePreset
        ? [...basePreset.footstepPoints]
        : createDefaultPoints(defaultFootstepFrequencies),
      gunLevel: basePreset?.gunLevel ?? 3,
      footstepLevel: basePreset?.footstepLevel ?? 3,
    }
    customPresets.value.push(newPreset)
    customCounter.value++
    currentPresetId.value = newPreset.id

    // Save to localStorage
    saveToStorage()
  }

  function deleteCustomPreset(presetId: string) {
    const index = customPresets.value.findIndex(p => p.id === presetId)
    if (index > -1) {
      customPresets.value.splice(index, 1)
      // Switch to first default preset
      currentPresetId.value = defaultPresets[0].id
      saveToStorage()
    }
  }

  function resetPreset(presetId: string) {
    const defaultPreset = defaultPresets.find(p => p.id === presetId)
    if (defaultPreset) {
      // Reset to default values
      defaultPreset.gunLevel = 3
      defaultPreset.footstepLevel = 3
      defaultPreset.gunPoints = createDefaultPoints(defaultGunFrequencies)
      defaultPreset.footstepPoints = createDefaultPoints(defaultFootstepFrequencies)
      if (enabled.value && currentPresetId.value === presetId) {
        applyCurrentPreset()
      }
    }
  }

  async function applyCurrentPreset() {
    if (!deviceStore.isConnected) return

    const preset = currentPreset.value
    if (!preset) return

    // Apply gun level gains
    const gunGainValue = levelToGain[preset.gunLevel]
    preset.gunPoints.forEach(point => {
      point.gain = gunGainValue
    })

    // Apply footstep level gains
    const footstepGainValue = levelToGain[preset.footstepLevel]
    preset.footstepPoints.forEach(point => {
      point.gain = footstepGainValue
    })

    const allPoints = [...preset.gunPoints, ...preset.footstepPoints]
    const commands = buildEqCurveCommands(allPoints)
    for (const command of commands) {
      await hidManager.sendData(command, false)
    }
  }

  function resetChiji() {
    // Send reset command (all gains to 0)
    const resetPoints = [
      ...createDefaultPoints(defaultGunFrequencies),
      ...createDefaultPoints(defaultFootstepFrequencies),
    ]
    const commands = buildEqCurveCommands(resetPoints)
    for (const command of commands) {
      hidManager.sendData(command, false)
    }
  }

  // Storage
  function saveToStorage() {
    localStorage.setItem('chijiCustomPresets', JSON.stringify(customPresets.value))
    localStorage.setItem('chijiCurrentPreset', currentPresetId.value)
    localStorage.setItem('chijiEnabled', enabled.value ? 'true' : 'false')
  }

  function loadFromStorage() {
    const stored = localStorage.getItem('chijiCustomPresets')
    if (stored) {
      try {
        customPresets.value = JSON.parse(stored)
        const maxNum = customPresets.value.reduce((max, p) => {
          const match = p.name.match(/自定义(\d+)/)
          return match ? Math.max(max, parseInt(match[1])) : max
        }, 0)
        customCounter.value = maxNum + 1
      } catch {
        customPresets.value = []
      }
    }

    const current = localStorage.getItem('chijiCurrentPreset')
    if (current) {
      currentPresetId.value = current
    }

    const enabledStored = localStorage.getItem('chijiEnabled')
    if (enabledStored === 'true') {
      enabled.value = true
    }
  }

  return {
    // State
    enabled,
    currentPresetId,
    customPresets,
    // Getters
    currentPreset,
    allPresets,
    isCustomPreset,
    gunGain,
    footstepGain,
    levelToGain,
    // Actions
    setEnabled,
    selectPreset,
    setGunLevel,
    setFootstepLevel,
    addCustomPreset,
    deleteCustomPreset,
    resetPreset,
    applyCurrentPreset,
    resetChiji,
    saveToStorage,
    loadFromStorage,
  }
})
